import { CONFIG } from './config.js';
import { DOM } from './state.js';
import { safeFetchClient } from './utils/safe-fetch-client.js';
import { KioscoGrid } from './components/kiosco/KioscoGrid.js';
import { PinPad, MAX_PIN_LEN } from './components/kiosco/PinPad.js';
import { showToast } from './components/shared/Toast.js';
import { formatElapsed, jornadaAbiertaExcedida, ACCIONES } from './shared/timesheet-policy.js';
import { KIOSCO_ERROR_MESSAGES } from './shared/kiosco-mensajes.js';

/**
 * Wiring de Fichaje, pestaña del panel de staff (staff-admin-access-split,
 * Slice A, AD9). Antes vivía en `/kiosco`/`#view-kiosco` como ruta standalone
 * (Slice 2); ahora es una sección más del registro de `js/admin.js`
 * (`SECTION_REGISTRY.fichaje`), montada dentro de `#admin-content-mount`.
 * MISMO módulo, mismo estado interno — solo cambió el punto de montaje:
 * módulo-nivel `viewModel`/`uiState`/`mutationInFlight`, listeners delegados
 * re-cableados en cada render, `showToast` para el éxito.
 *
 * ISOLATION BOUNDARY: nunca importa `supabase.client` — solo habla con
 * `/api/kiosco` (service_role del servidor), lo que hace `valor`/`pin_hash`
 * estructuralmente inalcanzables desde acá.
 */

let kioscoViewModel = null;                    // último GET /api/kiosco resuelto
let kioscoUiState = { status: 'loading' };     // loading | error | ready
let pinDialog = null;                          // { empleado, error? } | null
let mutationInFlight = false;                  // re-entrancy guard del submit
let refreshSeq = 0;                            // descarta un refresh viejo (patrón refreshEmpleados de admin.js)
let sessionSeq = 0;                            // sube en cada entrada/salida de la ruta: invalida TODA respuesta anterior
let pollTimer = null;
const POLL_MS = 30_000;
let elapsedTimer = null;                       // refresca "Xh Ym Ss" cada 1s (Kiosk UX round 2, 2026-09-02)
const ELAPSED_REFRESH_MS = 1_000;

/** Textos por código de `/api/kiosco` (dueño único: `js/shared/kiosco-mensajes.js`). */
const ERROR_MESSAGES = KIOSCO_ERROR_MESSAGES;
/** `duplicado`: ok:true pero NO escribió nada — nunca dice "entrada"/"salida" (Req: Double-Tap Protection). */
const DUPLICADO_MSG = 'No se registró nada. Probá de nuevo en un momento.';

// Re-pointed desde `DOM.kioscoContainer` (AD9): el mount ahora es el div que
// `mountFichajeSection` crea dentro de `#admin-content-mount` (reemplazado
// entero en cada cambio de tab), así que el guard post-`await`
// `if (!getMount()) return` empieza a hacer trabajo real en vez de ser
// cinturón-y-tiradores detrás de `sessionSeq`.
const getMount = () => DOM.adminContainer?.querySelector('#admin-fichaje-mount');

/**
 * Entry point, llamado desde `SECTION_REGISTRY.fichaje.mount` en js/admin.js
 * (`handleTabClick`/`renderActiveTab`). `mount` es el content mount del panel
 * (`#admin-content-mount`); acá se crea `#admin-fichaje-mount` adentro y se
 * arranca el estado exactamente como el viejo `renderKiosco()`.
 * @param {HTMLElement | null} mount
 */
export function mountFichajeSection(mount) {
    if (!mount) return;
    mount.innerHTML = '<div id="admin-fichaje-mount"></div>';
    stopPolling();
    detachPinKeydown();
    sessionSeq += 1;
    kioscoViewModel = null;
    kioscoUiState = { status: 'loading' };
    pinDialog = null;
    mutationInFlight = false;
    renderMount({ force: true });
    startPolling();
    startElapsedTicker();
    void refreshPresencia();
}

/**
 * Teardown al abandonar la pestaña Fichaje (AD8/AD9). Llamado por
 * `SECTION_REGISTRY.fichaje.teardown` en js/admin.js, vía
 * `teardownActiveSection()`. El content mount SE reemplaza al cambiar de
 * pestaña, pero sin esto el poll seguiría pegándole a la API cada 30s
 * mientras el admin usa cualquier otra sección (la terminal queda prendida
 * indefinidamente). Subir `sessionSeq` además invalida cualquier request en
 * vuelo: su respuesta ya no puede repintar nada.
 */
export function stopFichaje() {
    stopPolling();
    stopElapsedTicker();
    detachPinKeydown();
    sessionSeq += 1;
    pinDialog = null;
    mutationInFlight = false;
}

/**
 * Repinta grilla + diálogo de PIN y re-cablea listeners (el mount se reemplaza
 * entero cada vez).
 *
 * Con el diálogo de PIN abierto NO repinta salvo `force`: el pin pad siempre se
 * rinde con el input vacío, así que un repintado de fondo —el poll de 30s cae
 * mientras alguien está marcando su PIN— le borraría los dígitos tipeados sin
 * aviso, y esa persona terminaría mandando un PIN incompleto y recibiendo un
 * "PIN incorrecto" que no es suyo. `force` lo usan solo las acciones del propio
 * empleado (abrir, cerrar, cancelar, submit), que sí deben repintar.
 */
function renderMount({ force = false } = {}) {
    const mount = getMount();
    if (!mount) return;
    if (pinDialog && !force) return;
    mount.innerHTML = KioscoGrid(kioscoViewModel, kioscoUiState) + PinPad(pinDialog);
    bindMountListeners(mount);
}

function bindMountListeners(mount) {
    for (const btn of mount.querySelectorAll('[data-action="kiosco-empleado-seleccionar"]')) {
        btn.addEventListener('click', handleEmpleadoSeleccionar);
    }
    mount.querySelector('[data-action="kiosco-retry"]')?.addEventListener('click', () => void refreshPresencia());
    mount.querySelector('[data-action="kiosco-pin-cancelar"]')?.addEventListener('click', handlePinCancelar);
    for (const btn of mount.querySelectorAll('[data-action="kiosco-pin-digito"]')) {
        btn.addEventListener('click', handlePinDigito);
    }
    mount.querySelector('[data-action="kiosco-pin-borrar"]')?.addEventListener('click', handlePinBorrar);
    mount.querySelector('form[data-id]')?.addEventListener('submit', handlePinSubmit);
}

/**
 * Kiosk UX round 2 (2026-09-02, owner request 3): la acción se decide ACÁ,
 * al abrir el diálogo, según el `trabajando` que ya vino del último
 * `refreshPresencia()` — nunca un toggle silencioso en el servidor. El
 * servidor vuelve a chequear el estado real antes de escribir (409
 * `estado_cambiado` si el tablero quedó desactualizado); esto es solo la
 * intención del cliente.
 */
function handleEmpleadoSeleccionar(e) {
    const empleado = (kioscoViewModel?.empleados ?? []).find((emp) => emp.id === e.currentTarget.dataset.id);
    if (empleado) {
        const accion = empleado.trabajando ? ACCIONES.SALIDA : ACCIONES.ENTRADA;
        pinDialog = { empleado, accion };
        renderMount({ force: true });
        attachPinKeydown();
    }
}

function handlePinCancelar() {
    pinDialog = null;
    detachPinKeydown();
    renderMount({ force: true });
}

/** Escribe un dígito en `input.value` (propiedad, nunca serializado) y sincroniza los puntos. Compartido entre el click del teclado numérico y el teclado físico. */
function appendPinDigit(digit) {
    const input = getMount()?.querySelector('input[name="pin"]');
    if (input && input.value.length < MAX_PIN_LEN) {
        input.value += digit;
        syncPinDots(input.value.length);
    }
}

function removePinDigit() {
    const input = getMount()?.querySelector('input[name="pin"]');
    if (input) {
        input.value = input.value.slice(0, -1);
        syncPinDots(input.value.length);
    }
}

/**
 * Llena/vacía `[data-pin-dot]` vía `classList` — nunca los dígitos, solo
 * cuántos hay. L4: también habilita "Confirmar" recién con el PIN completo
 * (`MAX_PIN_LEN` dígitos) — antes un PIN de 3 viajaba al servidor y volvía un
 * error genérico.
 */
function syncPinDots(length) {
    const dots = getMount()?.querySelectorAll('[data-pin-dot]') ?? [];
    dots.forEach((dot, i) => {
        dot.classList.toggle('bg-brand', i < length);
        dot.classList.toggle('border-brand', i < length);
    });
    const confirmar = getMount()?.querySelector('[data-action="kiosco-pin-confirmar"]');
    if (confirmar) confirmar.disabled = length !== MAX_PIN_LEN;
}

function handlePinDigito(e) {
    appendPinDigit(e.currentTarget.dataset.digit);
}

function handlePinBorrar() {
    removePinDigit();
}

/**
 * Teclado físico (Kiosk UX follow-up, 2026-09-02): 0-9 escribe un dígito,
 * Backspace borra uno, Enter confirma, Escape cancela. UN solo listener de
 * `document`, atado en `attachPinKeydown`/desatado en `detachPinKeydown` —
 * nunca directo acá, así el mismo par sirve para abrir/cerrar el diálogo Y
 * para `stopFichaje` (AD8: cero listeners que sobrevivan un cambio de tab).
 */
function handlePinKeydown(e) {
    if (!pinDialog) return;
    // No tocar el PIN ni bloquear el atajo del navegador (Ctrl/Cmd/Alt+dígito,
    // ej. cambiar de pestaña) — Kiosk UX follow-up review-b590ffce9e134a5e finding 1.
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key >= '0' && e.key <= '9' && e.key.length === 1) {
        e.preventDefault();
        appendPinDigit(e.key);
    } else if (e.key === 'Backspace') {
        e.preventDefault();
        removePinDigit();
    } else if (e.key === 'Enter') {
        e.preventDefault();
        getMount()?.querySelector('form[data-id]')?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    } else if (e.key === 'Escape') {
        e.preventDefault();
        handlePinCancelar();
    }
}

let pinKeydownAttached = false;

function attachPinKeydown() {
    if (pinKeydownAttached) return;
    document.addEventListener('keydown', handlePinKeydown);
    pinKeydownAttached = true;
}

function detachPinKeydown() {
    if (!pinKeydownAttached) return;
    document.removeEventListener('keydown', handlePinKeydown);
    pinKeydownAttached = false;
}

/**
 * Envía la fichada. El PIN se lee y se limpia del input ANTES del round-trip
 * (solo-escritura: nunca queda en el DOM mientras la request está en vuelo).
 * Cada desenlace (duplicado/pin_invalido/rate_limited/etc.) tiene su propio
 * mensaje — nunca el genérico de otro caso.
 */
async function handlePinSubmit(e) {
    e.preventDefault();
    if (mutationInFlight) return;

    const empleadoId = e.currentTarget.dataset.id;
    const empleadoActual = pinDialog?.empleado;
    const accionActual = pinDialog?.accion;
    const input = getMount()?.querySelector('input[name="pin"]');
    const pin = input?.value ?? '';
    // L4: un PIN incompleto no viaja (Enter del teclado físico dispara el
    // submit aunque "Confirmar" esté deshabilitado). Los dígitos quedan.
    if (pin.length !== MAX_PIN_LEN) return;
    if (input) input.value = '';

    // La terminal es compartida y se pasa de mano en mano: mientras esta request
    // está en vuelo, quien la disparó puede cancelar, otra persona puede abrir su
    // propio diálogo, o alguien puede salir y volver a la ruta. Estos dos tokens
    // dicen si el diálogo que veremos al resolver SIGUE siendo el de esta acción.
    const sesion = sessionSeq;
    const sigueSiendoMiDialogo = () => sessionSeq === sesion && pinDialog?.empleado?.id === empleadoId;

    mutationInFlight = true;
    try {
        const res = await safeFetchClient(CONFIG.API.KIOSCO, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ empleado_id: empleadoId, pin, accion: accionActual }),
        });
        const data = await res.json().catch(() => ({}));
        if (!getMount() || sessionSeq !== sesion) return;

        if (data?.ok && data.action !== 'duplicado') {
            // La fichada SÍ se escribió: el aviso y el refresco del tablero van
            // siempre, aunque el diálogo ya no sea el de esta persona.
            const nombre = empleadoActual?.nombre ?? '';
            showToast(`${data.action === ACCIONES.ENTRADA ? 'Entrada' : 'Salida'} registrada, ${nombre}.`, { level: 'success' });
            void refreshPresencia();
            // El repintado forzado SOLO si el diálogo sigue siendo el de esta
            // acción. Si mientras tanto otra persona abrió el suyo, forzar acá
            // regeneraría el mount entero y le borraría los dígitos que ya
            // tipeó — el mismo daño que este arreglo evita en el poll, entrando
            // por la puerta del éxito ajeno. Sin `force`, el tablero nuevo se
            // ve apenas ese diálogo se cierre.
            if (!sigueSiendoMiDialogo()) return;
            pinDialog = null;
            detachPinKeydown();
            renderMount({ force: true });
            return;
        }

        // Kiosk UX round 2 (owner request 3): el tablero quedó desactualizado
        // (otra persona ya hizo esta acción, o la deshizo, desde otra
        // terminal/pestaña) — el servidor no escribió nada. Siempre se
        // refresca el tablero (el board mostraba un estado que ya no es
        // real), pero el diálogo se CIERRA en vez de reabrirse con un error:
        // reintentar con el mismo accion volvería a chocar contra el mismo
        // estado real.
        if (data?.ok === false && data.error === 'estado_cambiado') {
            void refreshPresencia();
            if (!sigueSiendoMiDialogo()) return;
            const nombre = empleadoActual?.nombre ?? '';
            const mensaje = data.estado === 'trabajando'
                ? `${nombre} ya tiene la entrada registrada. Se actualizó el tablero.`
                : `${nombre} no tiene una entrada abierta. Se actualizó el tablero.`;
            showToast(mensaje, { level: 'warning' });
            pinDialog = null;
            detachPinKeydown();
            renderMount({ force: true });
            return;
        }

        // Los desenlaces que reabren el diálogo con un error solo aplican si ese
        // diálogo todavía es el de esta acción: si no, estaríamos pisándole la
        // pantalla (y los dígitos ya tipeados) a otra persona.
        if (!sigueSiendoMiDialogo()) return;
        pinDialog = data?.ok
            ? { empleado: empleadoActual, accion: accionActual, error: DUPLICADO_MSG }
            : { empleado: empleadoActual, accion: accionActual, error: ERROR_MESSAGES[data?.error] ?? ERROR_MESSAGES.error_interno };
        renderMount({ force: true });
    } catch (err) {
        window.Sentry?.captureException(err);
        if (getMount() && empleadoActual && sigueSiendoMiDialogo()) {
            pinDialog = { empleado: empleadoActual, accion: accionActual, error: ERROR_MESSAGES.error_interno };
            renderMount({ force: true });
        }
    } finally {
        mutationInFlight = false;
    }
}

/** Recarga el tablero de presencia; `seq` descarta una respuesta vieja (mismo patrón que `refreshEmpleados` en admin.js). */
async function refreshPresencia() {
    const seq = ++refreshSeq;
    const sesion = sessionSeq;
    if (!kioscoViewModel) { kioscoUiState = { status: 'loading' }; renderMount(); }
    try {
        const res = await safeFetchClient(CONFIG.API.KIOSCO);
        if (!res.ok) throw new Error(`GET /api/kiosco failed: ${res.status}`);
        const data = await res.json();
        if (seq !== refreshSeq || sessionSeq !== sesion || !getMount()) return;
        kioscoViewModel = { empleados: data.empleados ?? [] };
        kioscoUiState = { status: 'ready' };
    } catch (err) {
        if (seq !== refreshSeq || sessionSeq !== sesion || !getMount()) return;
        window.Sentry?.captureException(err);
        kioscoUiState = { status: 'error' };
    }
    // Sin `force`: si hay un diálogo abierto no repinta (ver renderMount). El
    // tablero nuevo se ve apenas ese diálogo se cierra.
    renderMount();
}

function startPolling() {
    stopPolling();
    pollTimer = setInterval(() => { void refreshPresencia(); }, POLL_MS);
}

function stopPolling() {
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = null;
}

/**
 * Refresca los "Xh Ym Ss" de la grilla in-place, vía `textContent` sobre los
 * nodos `[data-elapsed-id]` que `KioscoGrid` ya renderizó — nunca llama a
 * `renderMount()`. A diferencia del poll de 30s, esto es seguro con el
 * diálogo de PIN abierto SIN necesitar el guard `pinDialog && !force`: la
 * grilla sigue en el DOM detrás del diálogo (`renderMount` concatena
 * `KioscoGrid + PinPad` en el mismo mount), así que tocar solo sus nodos de
 * texto nunca puede borrar los dígitos tipeados en el `<input name="pin">`.
 * Cada 1s (Kiosk UX round 2, 2026-09-02) — antes cada 60s, cuando el formato
 * no mostraba segundos.
 */
function updateElapsedTexts() {
    const mount = getMount();
    if (!mount || !kioscoViewModel) return;
    const nowMs = Date.now();
    for (const el of mount.querySelectorAll('[data-elapsed-id]')) {
        const empleado = kioscoViewModel.empleados.find((emp) => emp.id === el.dataset.elapsedId);
        const elapsed = empleado ? formatElapsed(empleado.desde, nowMs) : '';
        el.textContent = elapsed;
    }
    // F-B: el aviso de salida olvidada se prende in-place al cruzar las 12 h
    // (mismo tick, mismo `desde`, sin repintar la grilla ni el diálogo de PIN).
    for (const el of mount.querySelectorAll('[data-olvido-id]')) {
        const empleado = kioscoViewModel.empleados.find((emp) => emp.id === el.dataset.olvidoId);
        el.hidden = !(empleado?.trabajando && jornadaAbiertaExcedida(empleado.desde, nowMs));
    }
}

function startElapsedTicker() {
    stopElapsedTicker();
    elapsedTimer = setInterval(updateElapsedTexts, ELAPSED_REFRESH_MS);
}

function stopElapsedTicker() {
    if (elapsedTimer) clearInterval(elapsedTimer);
    elapsedTimer = null;
}
